import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { MyConfigService } from '../../../../services/my-config.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { OrganizationGatesVM } from '../../../../models/orderModel';
import Hls from 'hls.js';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../../environments/environment';
import { OpenGateFormComponent } from '../back-office24-gates/open-gate-form/open-gate-form.component';

@Component({
  selector: 'app-maintenance-gates',
  standalone: false,
  templateUrl: './maintenance-gates.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './maintenance-gates.component.css'
})
export class MaintenanceGatesComponent {
  componentDestroyed$: Subject<boolean> = new Subject();
  loading = false;
  activeRequests = 0;
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  cameras: any[];
  gates: any[] = [];
  parkings: any[] = [];
  selectedParkings: string[] = [];
  filteredCameras: any[] = [];
  parkingDisplay: string = "https://parking-display-test.mangocliff-b7a35cda.swedencentral.azurecontainerapps.io/";
  private hlsInstances: { [key: string]: Hls } = {};
  private cameraStates: { [key: string]: boolean } = {};
  openingGateCode: string | null = null;

  constructor(
    public myConfigService: MyConfigService,
    private BackOfficeService: BackOfficeService,
    private route: ActivatedRoute,
    public backOffice24StatesService: BackOffice24StatesService,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org
        org.config.parkings.forEach((element: any) => {
          this.parkings.push({
            value: element.parkingId,
            name: element.parkingName,
          });
        });
      }
    );
    this.getOrganizationGates();
  }

  setLoadingState(state: boolean) {
    this.loading = state;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  getParkingGates() {
    // If no parkings configured, create them from gates data
    if (this.parkings.length === 0) {
      const parkingMap = new Map<string, any>();

      this.cameras.forEach((cam: any) => {
        if (cam.hasBarrier && cam.parkings?.length > 0) {
          cam.parkings.forEach((item: any) => {
            if (!parkingMap.has(item?.id)) {
              parkingMap.set(item?.id, {
                value: item?.id,
                name: item?.name,
                gates: []
              });
            }
            parkingMap.get(item?.id).gates.push(cam);
          });
        }
      });

      this.parkings = Array.from(parkingMap.values());
    } else {
      this.parkings.forEach((element: any) => {
        element.gates = this.cameras.filter(
          (cam: any) => cam.hasBarrier && cam.parkings && cam.parkings[0] && cam.parkings[0]?.id && cam.parkings[0]?.id == (element.value)
        );
      });
    }

    const seenGates = new Map<string, boolean>();

    this.parkings = this.parkings.filter((parking: any) => {
      const gateKey = JSON.stringify(parking.gates.map((g: any) => g.id).sort());

      if (seenGates.has(gateKey)) {
        return false;
      }

      // Otherwise, mark this combination as seen and keep the parking
      seenGates.set(gateKey, true);
      return true;
    });
  }

  getOrganizationGates() {
    this.incrementRequests();
    this.BackOfficeService.getOrganizationMaintenanceGates(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(gates => {
        this.gates = gates;

        this.cameras = gates.flatMap((gate: any) =>
          gate.cameras.map((camera: any) => ({
            ...gate,
            camId: camera.sensorId,
            gateType: camera.type,
            // cameras: undefined
          }))
        )
          .map(gate => {
            const { cameras, ...rest } = gate;
            return rest;
          });


        this.filteredCameras = this.cameras;

        this.decrementRequests();
        this.getParkingGates();
      });
  }


  refreshGates() {
    this.getOrganizationGates();
  }

  getGateType(gateType: string) {
    if (gateType === 'Checkin') {
      return 'Infart';
    } else if (gateType === 'Checkout') {
      return 'Utfart';
    }
    return 'Unknown';
  }

  ngOnDestroy(): void {
    if (this.orgSubscription) {
      this.orgSubscription.unsubscribe();
    }
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  openGate(code: string): Promise<void> {
    this.openingGateCode = code;
    this.incrementRequests();

    return new Promise((resolve, reject) => {
      this.BackOfficeService.openParkingGate(this.organization.organizationId, code)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: () => {
            this.openingGateCode = null;
            resolve();
          },
          error: (error) => {
            this.openingGateCode = null;
            this.decrementRequests();
            reject(error);
          },
          complete: () => {
            this.decrementRequests();
          }
        });
    });
  }
}
